import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class EscapeManager {
  private handlers: Array<{
    id: symbol;
    handler: () => boolean; // ! returns true if handled
    priority: number;
    order: number; // ! registration order for LIFO tiebreaking
  }> = [];
  private listening = false;
  private counter = 0; // ! monotonic counter for registration order

  constructor() {
    this.startListening();
  }

  register(handler: () => boolean, priority = 0): symbol {
    const id = Symbol();
    this.handlers.push({
      id,
      handler,
      priority,
      order: this.counter++,
    });

    // ! Sort by priority first, then by order (LIFO - last registered wins)
    this.handlers.sort((a, b) => {
      if (b.priority !== a.priority) {
        return b.priority - a.priority; // ! higher priority first
      }
      return b.order - a.order; // ! last registered first (LIFO)
    });

    return id;
  }

  unregister(id: symbol): void {
    this.handlers = this.handlers.filter((h) => h.id !== id);
  }

  private handleEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;

    for (const { handler } of this.handlers) {
      if (handler()) {
        // ! Handler returned true - it handled the Escape, stop propagation
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        break;
      }
    }
  };

  private startListening(): void {
    if (this.listening) return;

    // ! listen in Capture phase since it is the First Phase
    window.addEventListener('keydown', this.handleEscape, {
      capture: true,
      passive: false,
    });
    this.listening = true;
  }
}

// * Escape key handler priority levels.
// * Higher priority runs first.
export const EscapePriority = {
  OVERLAY_CONTENT: 100 /* Dropdowns, autocomplete, overlays */,
  DIALOG_GUARD: 50 /* Dirty state, unsaved changes */,
  DIALOG_CLOSE: 40 /* Normal dialog close */,
  MODAL_CLOSE: 30 /* Modal/sidebar close */,
  PAGE_LEVEL: 10 /* Page-level shortcuts */,
  DEFAULT: 0 /* Fallback */,
} as const;
